import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { StatsPage } from "./Stats.js";
import type { ContentType, ModelRoute } from "../lib/types.js";

const CONTENT_TYPES: ContentType[] = ["text", "image", "svg", "audio", "video"];

const inputCls =
  "w-28 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

function pricingLabel(route: ModelRoute): string {
  const { pricing } = route;
  switch (pricing.unit) {
    case "subscription":
      return "included";
    case "token":
      return `$${pricing.inputPer1M ?? 0} in / $${pricing.outputPer1M ?? 0} out per 1M tok`;
    case "image":
      return `$${pricing.perUnit ?? 0} / image`;
    case "second":
      return `$${pricing.perUnit ?? 0} / sec`;
    case "char":
      return `$${pricing.perUnit ?? 0} / 1M chars`;
    default:
      return "";
  }
}

function RoutesTable() {
  const [routes, setRoutes] = useState<ModelRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api<{ items: ModelRoute[] }>("/api/routes");
      setRoutes(res.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load routes.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function patchRoute(routeId: string, patch: Partial<ModelRoute>) {
    setSavingId(routeId);
    try {
      const updated = await api<ModelRoute>(`/api/routes/${routeId}`, { method: "PATCH", json: patch });
      setRoutes((prev) => prev.map((r) => (r.routeId === routeId ? updated : r)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update the route.");
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div>
      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
      {loading && <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading...</p>}

      {!loading &&
        CONTENT_TYPES.map((ct) => {
          const group = routes.filter((r) => r.contentTypes.includes(ct));
          if (group.length === 0) return null;
          return (
            <section key={ct} className="mt-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{ct}</h2>
              <div className="mt-2 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-800">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2">Route</th>
                      <th className="px-3 py-2">Model id</th>
                      <th className="px-3 py-2">Pricing</th>
                      <th className="px-3 py-2">Notes</th>
                      <th className="px-3 py-2">Enabled</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {group.map((r) => (
                      <tr key={r.routeId} className="align-top">
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900 dark:text-gray-100">{r.displayName}</div>
                          <div className="text-xs text-gray-400">
                            {r.executor === "local" ? "local" : r.providerId}
                            {r.verifyBeforeEnable && (
                              <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                                verify model id
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            className={inputCls}
                            defaultValue={r.modelId}
                            disabled={savingId === r.routeId}
                            onBlur={(e) => {
                              if (e.target.value !== r.modelId) void patchRoute(r.routeId, { modelId: e.target.value });
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-300">{pricingLabel(r)}</td>
                        <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.notes}</td>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={r.enabled}
                            disabled={savingId === r.routeId}
                            onChange={(e) => void patchRoute(r.routeId, { enabled: e.target.checked })}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })}
    </div>
  );
}

export function RoutesPage() {
  const [tab, setTab] = useState<"routes" | "stats">("routes");
  const tabs: Array<{ key: typeof tab; label: string }> = [
    { key: "routes", label: "Routes" },
    { key: "stats", label: "Stats" },
  ];

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Routes</h1>
      <nav className="mt-4 flex gap-1 border-b border-gray-200 dark:border-gray-800">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === t.key
                ? "border-(--accent) font-medium text-(--accent)"
                : "border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>
      <div className="mt-4">{tab === "routes" ? <RoutesTable /> : <StatsPage />}</div>
    </div>
  );
}
